import { useEffect, useState } from 'react'
import { MONTHLY_DISPLAY_PRICE_AED, PLAN_FEATURES_EN } from '@/lib/products'
import {
  Zap,
  Sparkles,
  Check,
  Menu,
  X,
  ArrowRight,
  Share2,
  MessageCircle,
  Link as LinkIcon,
  Globe,
  Send,
  Feather,
} from 'lucide-react'

// ---------------------------------------------------------------------------
// Utility
// ---------------------------------------------------------------------------
const cn = (...classes: (string | boolean | undefined)[]) =>
  classes.filter(Boolean).join(' ')

// ---------------------------------------------------------------------------
// cogo's REAL infrastructure partners (mirrors the marketing landing §05 "built
// on"). Replaces the prior fabricated "trusted by" customer logos (Google /
// Nvidia / Nike / Microsoft / Amazon were never cogo customers) — truthful and
// consistent with cogo.xyz's own homepage.
// ---------------------------------------------------------------------------
const BUILT_ON = [
  { name: 'Stripe', mark: 'S', brand: '#635BFF' },
  { name: 'Supabase', mark: '⚡', brand: '#3ECF8E' },
  { name: 'Cloudflare', mark: '☁', brand: '#F38020' },
  { name: 'OpenAI', mark: '⊕', brand: '#10A37F' },
  { name: 'Anthropic', mark: '▲', brand: '#D97757' },
] as const

function BrandMark({ name, mark, brand }: { name: string; mark: string; brand: string }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className="flex h-6 w-6 items-center justify-center rounded-md text-xs font-bold text-white"
        style={{ backgroundColor: brand }}
        aria-hidden="true"
      >
        {mark}
      </span>
      <span className="text-sm font-medium text-muted-foreground">{name}</span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// 1. NAVBAR
// ---------------------------------------------------------------------------
function Navbar() {
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const navLinks = [
    { label: 'Features', href: '#features' },
    { label: 'Pricing', href: '#pricing' },
    { label: 'About', href: '#about' },
    { label: 'Docs', href: '#docs' },
  ]

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        scrolled
          ? 'bg-background/80 backdrop-blur-lg border-b border-border shadow-sm'
          : 'bg-transparent'
      )}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        {/* Logo */}
        <a href="/" className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary">
            <Zap className="h-4 w-4 text-primary-foreground" />
          </div>
          <span className="text-lg font-semibold text-foreground">cogo</span>
        </a>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {link.label}
            </a>
          ))}
        </div>

        {/* Desktop CTA */}
        <div className="hidden md:flex items-center gap-3">
          <a
            href="/studio"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Login
          </a>
          <a
            href="/studio"
            className="inline-flex items-center justify-center rounded-xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Sign Up
          </a>
        </div>

        {/* Mobile toggle */}
        <button
          className="md:hidden p-2 text-foreground"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background/95 backdrop-blur-lg border-b border-border px-6 pb-4">
          <div className="flex flex-col gap-3">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="text-sm text-muted-foreground hover:text-foreground py-2"
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <div className="flex gap-3 pt-2">
              <a
                href="/studio"
                className="text-sm text-muted-foreground hover:text-foreground py-2"
              >
                Login
              </a>
              <a
                href="/studio"
                className="inline-flex items-center justify-center rounded-xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
              >
                Sign Up
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}

// ---------------------------------------------------------------------------
// 2. HERO
// ---------------------------------------------------------------------------
function HeroSection() {
  return (
    <section className="relative overflow-hidden pt-32 pb-20 md:pt-40 md:pb-28">
      {/* Background gradient */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[600px] w-[800px] bg-primary/10 rounded-full blur-3xl opacity-30" />
      </div>

      <div className="mx-auto max-w-7xl px-6 text-center">
        {/* Badge */}
        <div className="animate-appear inline-flex items-center gap-2 rounded-full border border-border bg-muted/50 px-4 py-1.5 text-sm text-muted-foreground mb-8">
          <Sparkles className="h-3.5 w-3.5 text-primary" />
          <span>From one sentence to web · iOS · Android</span>
        </div>

        {/* Heading */}
        <h1 className="animate-slide-up mx-auto max-w-4xl text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
          Describe it once.{' '}
          <span className="text-primary">Ship to web and mobile.</span>
        </h1>

        {/* Subtitle */}
        <p className="animate-slide-up mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
          cogo turns one description into a working app — running on web, iOS, and Android from the
          same source. Every block is validated before it ships, with real data and payments.
        </p>

        {/* CTAs */}
        <div className="animate-slide-up mt-10 flex flex-wrap items-center justify-center gap-4">
          <a
            href="/studio"
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors shadow-md"
          >
            Start building
            <ArrowRight className="h-4 w-4" />
          </a>
          <a
            href="/templates"
            className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-6 py-3 text-sm font-medium text-foreground hover:bg-muted transition-colors"
          >
            Browse templates
          </a>
        </div>

        {/* App screenshot placeholder */}
        <div className="animate-fade-in mx-auto mt-16 max-w-5xl">
          <div className="relative rounded-xl border border-border bg-muted/30 p-2 shadow-2xl">
            <div className="rounded-xl bg-gradient-to-br from-muted to-muted/50 aspect-[16/9] flex items-center justify-center">
              <div className="text-center">
                <div className="mx-auto h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                  <Zap className="h-8 w-8 text-primary" />
                </div>
                <p className="text-sm text-muted-foreground">Application Preview</p>
              </div>
            </div>
          </div>
        </div>

        {/* Built on — cogo's real infrastructure stack (truthful) */}
        <div className="mt-20">
          <p className="text-sm text-muted-foreground mb-8">Built on</p>
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
            {BUILT_ON.map((p) => (
              <BrandMark key={p.name} name={p.name} mark={p.mark} brand={p.brand} />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// 3. FEATURES
// ---------------------------------------------------------------------------

function FeatureIconDecorator({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative mb-6">
      {/* Grid pattern background */}
      <div className="absolute -inset-2 rounded-xl opacity-20">
        <div
          className="h-full w-full"
          style={{
            backgroundImage:
              'radial-gradient(circle, hsl(var(--primary) / 0.3) 1px, transparent 1px)',
            backgroundSize: '8px 8px',
          }}
        />
      </div>
      <div className="relative flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 border border-primary/20">
        {children}
      </div>
    </div>
  )
}

const features = [
  {
    icon: <Globe className="h-6 w-6 text-primary" />,
    title: 'Web and mobile, one source',
    description:
      'One sentence becomes a public web URL and an iOS / Android build, both rendered from the same cogo JSON. Most AI builders ship web only — your second build is on you.',
  },
  {
    icon: <Check className="h-6 w-6 text-primary" />,
    title: 'AI builds. Validators keep it valid.',
    description:
      'Every block is checked against schema, ontology (SHACL), and ActionFlow rules. Off-vocabulary actions or missing event bindings are rejected and re-prompted, so malformed apps never ship.',
  },
  {
    icon: <Sparkles className="h-6 w-6 text-primary" />,
    title: 'One vocabulary, every surface',
    description:
      'Marketing pages, the studio chrome, and your generated apps share one component library and the same design tokens — closed vocabulary, open expression, guarded against drift.',
  },
]

function FeaturesSection() {
  return (
    <section id="features" className="py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-6">
        {/* Heading */}
        <div className="mx-auto max-w-2xl text-center mb-16">
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Three things every other AI builder skips
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Web-only output, ungoverned LLM hallucinations, and design tokens that drift across
            surfaces. cogo gets all three right.
          </p>
        </div>

        {/* Cards */}
        <div className="grid gap-8 md:grid-cols-3">
          {features.map((feature) => (
            <div
              key={feature.title}
              className="group rounded-xl border border-border bg-card p-8 transition-all hover:shadow-lg hover:border-primary/30"
            >
              <FeatureIconDecorator>{feature.icon}</FeatureIconDecorator>
              <h3 className="text-lg font-semibold text-card-foreground mb-3">
                {feature.title}
              </h3>
              <p className="text-sm leading-relaxed text-muted-foreground">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// 5. PRICING
// ---------------------------------------------------------------------------

// cogo's real Creator plan features (single-sourced from lib/products) so the
// /compare teaser matches the live AED catalog instead of placeholder copy.
const pricingFeatures = [...PLAN_FEATURES_EN.creator]

function PricingSection() {
  return (
    <section id="pricing" className="py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-6">
        {/* Heading */}
        <div className="mx-auto max-w-2xl text-center mb-16">
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Pricing that scales with your business
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Start free, upgrade when you need to. No hidden fees, no surprises.
          </p>
        </div>

        {/* Single large pricing card */}
        <div className="mx-auto max-w-4xl">
          <div className="rounded-2xl border border-border bg-card p-8 md:p-12 shadow-lg">
            <div className="grid gap-10 md:grid-cols-2">
              {/* Left: plan info */}
              <div className="flex flex-col justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary mb-4">
                    Most Popular
                  </div>
                  <h3 className="text-2xl font-bold text-card-foreground">Creator</h3>
                  <div className="mt-4 flex items-baseline gap-2">
                    <span className="text-5xl font-bold tracking-tight text-card-foreground">
                      {MONTHLY_DISPLAY_PRICE_AED.creator}
                    </span>
                    <span className="text-muted-foreground">/month</span>
                  </div>
                  <p className="mt-4 text-sm text-muted-foreground">
                    Best suited for influencers launching multiple campaigns. See all plans
                    on the pricing page.
                  </p>
                </div>
                <div className="mt-8">
                  <a
                    href="/pricing"
                    className="inline-flex w-full items-center justify-center rounded-xl bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                  >
                    Get Started
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </div>
              </div>

              {/* Right: feature checklist */}
              <div>
                <p className="text-sm font-medium text-card-foreground mb-4">
                  Everything included:
                </p>
                <ul className="space-y-3">
                  {pricingFeatures.map((feature) => (
                    <li key={feature} className="flex items-center gap-3 text-sm text-muted-foreground">
                      <Check className="h-4 w-4 text-primary flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Built on — cogo's real infrastructure stack (truthful) */}
                <div className="mt-8 pt-6 border-t border-border">
                  <p className="text-xs text-muted-foreground mb-3">Built on</p>
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                    {BUILT_ON.map((p) => (
                      <BrandMark key={p.name} name={p.name} mark={p.mark} brand={p.brand} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// 6. CALL TO ACTION
// ---------------------------------------------------------------------------
function CTASection() {
  return (
    <section className="py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-6">
        <div className="relative overflow-hidden rounded-2xl bg-primary px-8 py-16 md:px-16 md:py-24 text-center">
          {/* Background decoration */}
          <div className="absolute inset-0 -z-0">
            <div className="absolute top-0 right-0 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
            <div className="absolute bottom-0 left-0 h-64 w-64 rounded-full bg-white/5 blur-3xl" />
          </div>

          <div className="relative z-10">
            <h2 className="text-3xl font-bold tracking-tight text-primary-foreground sm:text-4xl">
              Start Building
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-primary-foreground/80">
              Type one sentence. Preview, validate, and publish to web and mobile — no second
              build, no second team.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <a
                href="/studio"
                className="inline-flex items-center gap-2 rounded-xl bg-background px-6 py-3 text-sm font-medium text-foreground hover:bg-background/90 transition-colors shadow-md"
              >
                Start building
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="/pricing"
                className="inline-flex items-center gap-2 rounded-xl border border-primary-foreground/30 px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-white/10 transition-colors"
              >
                See pricing
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// 7. FOOTER
// ---------------------------------------------------------------------------
function Footer() {
  const footerLinks = ['Features', 'Solution', 'Customers', 'Pricing', 'Help', 'About']
  const socialIcons = [
    { icon: <Share2 className="h-4 w-4" />, label: 'Share' },
    { icon: <MessageCircle className="h-4 w-4" />, label: 'Chat' },
    { icon: <LinkIcon className="h-4 w-4" />, label: 'Link' },
    { icon: <Globe className="h-4 w-4" />, label: 'Website' },
    { icon: <Send className="h-4 w-4" />, label: 'Send' },
    { icon: <Feather className="h-4 w-4" />, label: 'Blog' },
  ]

  return (
    <footer className="border-t border-border bg-muted/20">
      <div className="mx-auto max-w-7xl px-6 py-12">
        {/* Logo + description */}
        <div className="flex flex-col items-center text-center mb-10">
          <a href="/" className="flex items-center gap-2 mb-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary">
              <Zap className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="text-lg font-semibold text-foreground">cogo</span>
          </a>
          <p className="max-w-sm text-sm text-muted-foreground">
            Modern solutions for customer engagement, powered by AI.
          </p>
        </div>

        {/* Links row */}
        <div className="flex flex-wrap items-center justify-center gap-6 mb-8">
          {footerLinks.map((link) => (
            <a
              key={link}
              href={`#${link.toLowerCase()}`}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {link}
            </a>
          ))}
        </div>

        {/* Social icons */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {socialIcons.map((social) => (
            <a
              key={social.label}
              href="#"
              className="flex h-9 w-9 items-center justify-center rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              aria-label={social.label}
            >
              {social.icon}
            </a>
          ))}
        </div>

        {/* Copyright */}
        <div className="text-center">
          <p className="text-xs text-muted-foreground">
            &copy; {new Date().getFullYear()} cogo. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  )
}

// ---------------------------------------------------------------------------
// ASSEMBLED LANDING PAGE
// ---------------------------------------------------------------------------
export function TailarkLandingPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <main>
        <HeroSection />
        <FeaturesSection />
        <PricingSection />
        <CTASection />
      </main>
      <Footer />
    </div>
  )
}
